import { useEffect, useRef, useState } from "react";
import { Send, Sparkles, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { PageHeader } from "../components/Shared";
import { API, api } from "../lib/api";

const PROMPTS = [
  "Rédige un e-mail de relance pour un devis envoyé il y a 5 jours",
  "Le client trouve notre devis trop cher, comment répondre ?",
  "Calcule le surcoût pour un piano droit au 3e étage sans ascenseur",
  "Quelles formalités pour un déménagement France → Allemagne ?",
];

function Bubble({ m }) {
  const mine = m.role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div data-testid={`chat-message-${m.role}`} className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed ${mine ? "bg-[#0b2c6b] text-white" : "border border-slate-200 bg-white text-slate-800"}`}>
        {m.content || <Loader2 className="h-4 w-4 animate-spin text-teal-500" />}
      </div>
    </div>
  );
}

export default function Assistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/ai/history").then((r) => setMessages(r.data));
  }, []);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/ai/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message: msg }) });
      if (!res.ok) throw new Error(res.status === 401 ? "Session expirée, reconnectez-vous." : "Erreur de l'assistant");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.error) toast.error("L'assistant a rencontré une erreur.");
          if (ev.delta) setMessages((m) => [...m.slice(0, -1), { role: "assistant", content: m[m.length - 1].content + ev.delta }]);
        }
      }
    } catch (e) {
      toast.error(e.message);
      setMessages((m) => m.slice(0, -1));
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    await api.delete("/ai/history");
    setMessages([]);
  };

  return (
    <div className="fade-up flex h-[calc(100vh-8rem)] flex-col">
      <PageHeader overline="Claude IA" title="Assistant commercial" subtitle="Connaît vos tarifs et vos devis en attente.">
        <Button variant="outline" data-testid="chat-clear-button" onClick={clear} disabled={!messages.length || busy}><Trash2 className="mr-2 h-4 w-4" />Effacer</Button>
      </PageHeader>
      <div className="flex-1 space-y-4 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <div className="brand-gradient mb-4 flex h-12 w-12 items-center justify-center rounded-2xl text-white"><Sparkles className="h-6 w-6" /></div>
            <p className="mb-6 text-sm text-slate-500">Que puis-je préparer pour vous ?</p>
            <div className="grid max-w-2xl gap-2 sm:grid-cols-2">
              {PROMPTS.map((p, i) => (
                <button key={p} data-testid={`chat-suggestion-${i}`} onClick={() => send(p)} className="card-hover rounded-xl border border-slate-200 bg-white p-3 text-left text-sm text-slate-700">{p}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => <Bubble key={m.id || i} m={m} />)}
        <div ref={endRef} />
      </div>
      <form className="mt-4 flex gap-2" onSubmit={(e) => { e.preventDefault(); send(); }}>
        <Textarea data-testid="chat-input" rows={2} value={input} onChange={(e) => setInput(e.target.value)} placeholder="Posez votre question…" className="resize-none bg-white"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
        <Button type="submit" data-testid="chat-send-button" disabled={busy || !input.trim()} className="h-auto bg-[#0b2c6b] px-5 hover:bg-[#1559d6]">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </form>
    </div>
  );
}
